import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography } from '@mui/material';

import { FBoxContainer, FHInputField, FProgressBar, FHSelect, FHTextarea } from '../../../base';
import { educationBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import {
  cleanNumericInputValue,
  getAllInvalidFieldNames,
  validateSchema,
  validateSync,
} from '../../../../helpers';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../constants';
import { educationBackgroundInitialState, EducationBackgroundSchema } from './validationSchema';
import { EducationEvaluations, EducationSettings } from './constants';

import type { IEducationBackground, SelectValueType } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

enum Iep504 {
  na = 'N/A',
  iep = 'IEP',
  '5o4' = '504',
}

function EducationBackground() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    editingEducationBackground,
    loading: educationBackgroundLoading,
    treatmentPlanId: currentTPId,
  } = useSelector((state: any) => state.educationBackgrounds);
  const isEditable = useTreatmentPlanEditable() && !educationBackgroundLoading;
  const [educationBackground, setEducationBackground] = useState<IEducationBackground | null>(null);
  const [iep504, setIep504] = useState<Iep504>(Iep504['na']);
  const [errors, setErrors] = useState<any>({});
  const [showOldEducationSettingsField, setShowOldEducationSettingsField] =
    useState<boolean>(false);
  const [showOldEducationEvaluationField, setShowOldEducationEvaluationField] =
    useState<boolean>(false);

  const listOfValidationKeys = [
    'education_setting',
    'education_support',
    'hours_per_week',
    'education_description',
    'focus_area_description',
    'education_evaluation',
  ];
  useEffect(() => {
    if (!editingEducationBackground) return;
    const isNewEducationSetting = Object.values(EducationSettings).some(
      (option: string) => option === editingEducationBackground.education_setting,
    );
    const isNewEducationEvaluation = Object.values(EducationEvaluations).some(
      (option: string) => option === editingEducationBackground.education_evaluation,
    );

    // NOTE: education_setting & education_evaluation fields has been changed from input field(free text) to select dropdown(enum values)
    // BE only allows the enum values on POST/PATCH endpoints
    // The old free texts only will be displayed on non-editable(view mode) TP and will be removed on editing mode. BCBA needs to re-select enum option from the dropdown in this case.
    setShowOldEducationSettingsField(!isEditable && !isNewEducationSetting);
    setShowOldEducationEvaluationField(!isEditable && !isNewEducationEvaluation);
  }, [isEditable, editingEducationBackground]);

  useEffect(() => {
    const educationBackgroundsErrors = getAllInvalidFieldNames(
      EducationBackgroundSchema,
      educationBackgroundInitialState,
    );
    setErrors((prev: any) => ({ ...prev, ...educationBackgroundsErrors }));
    if (!educationBackground) {
      return;
    }
    const { is_504: _is_504, is_iep: _is_iep, ...fieldsToBeValidated } = educationBackground;
    //Removing the two fields that are being prefilled
    if (!fieldsToBeValidated || Object.keys(fieldsToBeValidated).length === 0) {
      return;
    }
    listOfValidationKeys.forEach((key: string) => {
      handleValidate(key, educationBackground);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationBackground]);

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(educationBackgroundActions.clearState());
      return;
    }
    if (currentTPId !== treatmentPlan?.id) {
      dispatch(
        educationBackgroundActions.updateState({ key: 'editingEducationBackground', data: null }),
      );
    }
    // Check if there is an editing education background for current treatment plan
    if (editingEducationBackground && currentTPId === treatmentPlan?.id) {
      setEducationBackground({
        ...editingEducationBackground,
        is_iep: editingEducationBackground['is_iep'] || false,
        is_504: editingEducationBackground['is_504'] || false,
      });
      const iep = editingEducationBackground['is_504']
        ? Iep504['5o4']
        : editingEducationBackground['is_iep']
          ? Iep504.iep
          : Iep504.na;
      setIep504(iep);
      return;
    }

    fetchEducationBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan, editingEducationBackground]);

  useEffect(() => {
    if (!treatmentPlan) return;

    if (Object.keys(errors).length === 0) {
      dispatch(
        educationBackgroundActions.updateState({
          key: 'errorCounts',
          data: 0,
        }),
      );
      return;
    }

    const errorCount = Object.keys(errors).filter((key) => errors[key]).length;
    dispatch(
      educationBackgroundActions.updateState({
        key: 'errorCounts',
        data: errorCount,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const fetchEducationBackground = async () => {
    const { payload } = await dispatch(
      educationBackgroundActions.getEducationBackground({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );

    setEducationBackground({
      ...payload,
      is_iep: payload?.['is_iep'] || false,
      is_504: payload?.['is_504'] || false,
    });
    const iep = payload?.is_504 ? Iep504['5o4'] : payload?.is_iep ? Iep504.iep : Iep504.na;
    setIep504(iep);
  };

  const handleUpdateField = (key: string, value: any) => {
    setEducationBackground((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async (requestData?: IEducationBackground) => {
    if (!treatmentPlan) return;

    const data = requestData ?? educationBackground;
    if (!data) return;
    const isValid = validateSync(EducationBackgroundSchema, data);
    if (isValid) {
      // Update education background
      if (data.id) {
        const { payload } = await dispatch(
          educationBackgroundActions.updateEducationBackground({
            treatmentPlanId: treatmentPlan.id,
            data,
            getAccessTokenSilently,
          }),
        );
        if (!payload) return;

        setEducationBackground(payload);
      } else {
        // Create new education background
        const { payload } = await dispatch(
          educationBackgroundActions.createEducationBackground({
            treatmentPlanId: treatmentPlan.id,
            data,
            getAccessTokenSilently,
          }),
        );
        if (!payload) return;

        setEducationBackground(payload);
      }
    } else {
      dispatch(
        educationBackgroundActions.updateState({
          key: 'editingEducationBackground',
          data,
        }),
      );
    }
  };

  const handleValidate = (key: string, data: any = educationBackground) => {
    if (!data) return;

    const isValid = validateSchema(EducationBackgroundSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleUpdateIep504 = (value: Iep504) => {
    if (!value || !educationBackground) return;

    setIep504(value);

    switch (value) {
      case Iep504['na']:
        handleUpdateField('is_iep', false);
        handleUpdateField('is_504', false);
        handleSave({ ...educationBackground, is_iep: false, is_504: false });
        break;
      case Iep504['iep']:
        handleUpdateField('is_iep', true);
        handleUpdateField('is_504', false);
        handleSave({ ...educationBackground, is_iep: true, is_504: false });
        break;
      case Iep504['5o4']:
        handleUpdateField('is_iep', false);
        handleUpdateField('is_504', true);
        handleSave({ ...educationBackground, is_iep: false, is_504: true });
        break;
      default:
        break;
    }
  };

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Education background" sx={{ mt: 3 }}>
      {educationBackgroundLoading && <FProgressBar />}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box mb={1.5}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Education setting*
            </Typography>
            {showOldEducationSettingsField ? (
              <FHTextarea
                fullWidth
                minRows={5}
                maxRows={5}
                minLength={10}
                colorTheme={Themes.Primary}
                expandable={isEditable}
                disabled={true}
                value={educationBackground?.education_setting || ''}
              />
            ) : (
              <FHSelect
                data-testid="educationSettingDropDown"
                fullWidth
                inputHeight={40}
                colorTheme={Themes.Primary}
                displayEmpty
                disabled={!isEditable}
                renderValue={
                  !educationBackground?.education_setting
                    ? () => <Typography>Select Education Setting</Typography>
                    : undefined
                }
                options={Object.values(EducationSettings).map((option: string) => ({
                  value: option,
                  label: option,
                }))}
                optionDataTestIdPrefix="educationSettingOption"
                value={educationBackground?.education_setting || ''}
                onUpdate={(value: SelectValueType) => {
                  const educationSetting = Object.values(EducationSettings).find(
                    (option) => option === value,
                  );
                  if (!educationSetting || !educationBackground) return;
                  handleUpdateField('education_setting', educationSetting);
                  handleSave({
                    ...educationBackground,
                    education_setting: educationSetting,
                  });
                }}
                error={errors && errors['education_setting']}
              />
            )}
          </Box>
          <Box my={1.5}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Education description*
            </Typography>
            <FHTextarea
              data-testid="educationDescriptionTextField"
              fullWidth
              minRows={3}
              maxRows={3}
              colorTheme={Themes.Primary}
              expandable={isEditable}
              disabled={!isEditable}
              value={educationBackground?.education_description || ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleUpdateField('education_description', e.target.value)
              }
              onBlur={() => handleSave()}
              onCloseExpandDialog={() => handleSave()}
              error={errors['education_description']}
            />
          </Box>
          <Box>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Focus area description*
            </Typography>
            <FHTextarea
              data-testid="focusAreaDescriptionTextField"
              fullWidth
              minRows={3}
              maxRows={3}
              colorTheme={Themes.Primary}
              expandable={isEditable}
              disabled={!isEditable}
              value={educationBackground?.focus_area_description || ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleUpdateField('focus_area_description', e.target.value)
              }
              onBlur={() => handleSave()}
              onCloseExpandDialog={() => handleSave()}
              error={errors['focus_area_description']}
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Education supports*
            </Typography>
            <FHInputField
              fullWidth
              colorTheme={Themes.Primary}
              inputHeight={40}
              disabled={!isEditable}
              value={educationBackground?.education_support || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleUpdateField('education_support', e.target.value)
              }
              onBlur={() => handleSave()}
              error={errors['education_support']}
            />
          </Box>
          <Box mt={1}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Number of hours per week*
            </Typography>
            <FHInputField
              data-testid="hoursPerWeekTextField"
              fullWidth
              colorTheme={Themes.Primary}
              inputHeight={40}
              disabled={!isEditable}
              value={educationBackground?.hours_per_week || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleUpdateField('hours_per_week', cleanNumericInputValue(e.target.value))
              }
              onBlur={() => handleSave()}
              error={errors['hours_per_week']}
            />
          </Box>
          <Box my={showOldEducationEvaluationField ? 2.5 : 1.5}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              IEP or 504 in place?*
            </Typography>
            <FHSelect
              data-testid="iep504DropDown"
              fullWidth
              colorTheme={Themes.Primary}
              inputHeight={40}
              disabled={!isEditable}
              options={[
                { value: Iep504['na'], label: 'N/A' },
                { value: Iep504['iep'], label: 'IEP' },
                { value: Iep504['5o4'], label: '504' },
              ]}
              optionDataTestIdPrefix="iep504Option"
              value={iep504}
              onUpdate={(value: SelectValueType) => {
                const iep504Value = Object.values(Iep504).find((option) => option === value);
                if (iep504Value) handleUpdateIep504(iep504Value);
              }}
            />
          </Box>
          <Box mt={showOldEducationEvaluationField ? 6.8 : 1.5}>
            <Typography fontWeight="bold" mb={1} textAlign="left">
              Client&apos;s education evaluation*
            </Typography>
            {showOldEducationEvaluationField ? (
              <FHTextarea
                fullWidth
                minRows={3}
                maxRows={3}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                colorTheme={Themes.Primary}
                expandable={isEditable}
                disabled={true}
                value={educationBackground?.education_evaluation || ''}
              />
            ) : (
              <FHSelect
                data-testid="educationEvaluationDropDown"
                fullWidth
                displayEmpty
                inputHeight={40}
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                options={Object.values(EducationEvaluations).map((option: string) => ({
                  value: option,
                  label: option,
                }))}
                optionDataTestIdPrefix="educationEvaluationOption"
                renderValue={
                  !educationBackground?.education_evaluation
                    ? () => <Typography>Select Evaluation</Typography>
                    : undefined
                }
                value={educationBackground?.education_evaluation || ''}
                onUpdate={(value: SelectValueType) => {
                  const educationEvaluation = Object.values(EducationEvaluations).find(
                    (option) => option === value,
                  );
                  if (!educationEvaluation || !educationBackground) return;
                  handleUpdateField('education_evaluation', educationEvaluation);
                  handleSave({
                    ...educationBackground,
                    education_evaluation: educationEvaluation,
                  });
                }}
                error={errors && errors['education_evaluation']}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </FBoxContainer>
  );
}

export default EducationBackground;
